import { useEffect, useRef, useState, type KeyboardEvent, type ChangeEvent, type DragEvent } from "react";
import { toast } from "@/lib/toast";
import { Paperclip, SendHorizontal, Square, X, FileText, Database, Globe, Combine, Cpu } from "lucide-react";
import type { ChatModelOption } from "@/api/types";

const MAX_MB = 50;
const MAX_FILES = 5;
const ALLOWED = /\.(pdf|docx?|xlsx?|pptx?|txt|csv|hwp|png|jpe?g|gif|webp|bmp)$/i;

// 검색 방법 — 선택 시 질문 앞에 해당 prefix를 붙여 백엔드 라우팅과 연동한다.
// (기존 [웹검색]/[하이브리드] prefix 메커니즘을 UI로 노출)
type SearchMode = "kb" | "web" | "hybrid";
const SEARCH_MODES: { key: SearchMode; label: string; prefix: string; icon: typeof Database; hint: string }[] = [
  { key: "kb", label: "지식저장소", prefix: "", icon: Database, hint: "사내 지식 저장소에서만 답변" },
  { key: "web", label: "웹검색", prefix: "[웹검색] ", icon: Globe, hint: "외부 웹에서 검색해 바로 답변" },
  { key: "hybrid", label: "하이브리드", prefix: "[하이브리드] ", icon: Combine, hint: "사내 자료를 근거로 웹 검색해 바로 답변" },
];

// 첨부 시 노출되는 스킬 빠른 실행 버튼(명시적 트리거)
// minFiles: 해당 스킬 실행에 필요한 최소 첨부 수
const SKILL_CHIPS: { hint: string; label: string; minFiles: number }[] = [
  { hint: "meeting", label: "회의록 작성(요약식)", minFiles: 1 },
  { hint: "meeting_transcript", label: "회의록 작성(녹취식)", minFiles: 1 },
  { hint: "summary", label: "요약서 작성", minFiles: 1 },
  { hint: "translate", label: "번역서 작성", minFiles: 1 },
  { hint: "analyze", label: "분석서 작성", minFiles: 1 },
];

export default function Composer({
  busy,
  onSend,
  onStop,
  onSkillSend,
  seed,
  models = [],
  modelId = null,
  onModelChange,
}: {
  busy: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  onSkillSend: (files: File[], text: string, hint: string) => void;
  seed?: { text: string; nonce: number };
  // 답변 모델 셀렉터(관리자 등록 시에만 노출). 선택 상태는 부모(Chat)가 관리 —
  // 전송 후 기본값 모델로 무조건 복귀하는 정책을 부모가 보장한다.
  models?: ChatModelOption[];
  modelId?: number | null;
  onModelChange?: (id: number) => void;
}) {
  const [text, setText] = useState("");
  const [mode, setMode] = useState<SearchMode>("kb");   // 검색 방법(기본: 지식저장소)
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // 메시지 편집 → 입력창에 채우기(부모가 seed.nonce 변경)
  useEffect(() => {
    if (seed) {
      setText(seed.text);
      requestAnimationFrame(() => {
        const ta = taRef.current;
        if (ta) {
          ta.focus();
          ta.style.height = "auto";
          ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed?.nonce]);

  const resetHeight = () => {
    if (taRef.current) taRef.current.style.height = "auto";
  };
  const autoGrow = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
  };

  const clearAll = () => {
    setPendingFiles([]);
    setText("");
    resetHeight();
  };

  const submit = () => {
    if (busy) return;
    const v = text.trim();
    // 검색 방법 prefix 부가(지식저장소면 무변경). 백엔드가 prefix로 웹/하이브리드 라우팅.
    const prefix = SEARCH_MODES.find((s) => s.key === mode)?.prefix ?? "";
    if (pendingFiles.length > 0) {
      // 첨부가 있으면 스킬 경로로. 검색 방법 prefix를 함께 전달 —
      // 서버는 prefix 또는 스킬 키워드 미매칭 시 qa(route_to_qa)로 판정하고,
      // 첨부는 세션문서로 업로드되어 질의(웹검색/하이브리드 포함)의 컨텍스트로 쓰인다.
      onSkillSend(pendingFiles, v ? prefix + v : "", "");
      clearAll();
      return;
    }
    if (!v) return;
    onSend(prefix + v);
    setText("");
    resetHeight();
  };

  const runChip = (hint: string, minFiles: number) => {
    if (busy || pendingFiles.length === 0) return;
    if (pendingFiles.length < minFiles) {
      const label = SKILL_CHIPS.find((c) => c.hint === hint)?.label ?? hint;
      toast(`'${label}'은(는) 파일이 ${minFiles}개 이상 필요합니다.`, "error");
      return;
    }
    onSkillSend(pendingFiles, text.trim(), hint);
    clearAll();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // 파일 추가(검증 + 중복/개수 제한). 기존 목록에 누적.
  const addFiles = (incoming: File[]) => {
    setPendingFiles((prev) => {
      const next = [...prev];
      for (const f of incoming) {
        if (!ALLOWED.test(f.name)) {
          toast(`지원하지 않는 형식: ${f.name}`, "error");
          continue;
        }
        if (f.size > MAX_MB * 1024 * 1024) {
          toast(`${f.name}: ${MAX_MB}MB 이하만 가능합니다.`, "error");
          continue;
        }
        if (next.some((p) => p.name === f.name && p.size === f.size)) continue; // 중복 제거
        if (next.length >= MAX_FILES) {
          toast(`최대 ${MAX_FILES}개까지 첨부할 수 있습니다.`, "error");
          break;
        }
        next.push(f);
      }
      return next;
    });
  };

  const onPickFile = (e: ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (list.length) addFiles(list);
  };

  const removeAt = (idx: number) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  // ── 드래그앤드롭 ──
  const onDragOver = (e: DragEvent) => {
    if (busy) return;
    if (Array.from(e.dataTransfer.types || []).includes("Files")) {
      e.preventDefault();
      setDragOver(true);
    }
  };
  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    const list = Array.from(e.dataTransfer.files ?? []);
    if (list.length) addFiles(list);
  };

  const has = pendingFiles.length > 0;

  return (
    <div
      className="relative border-t border-border bg-background/90 backdrop-blur pb-[env(safe-area-inset-bottom)]"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-10 m-2 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-primary/5 text-sm font-medium text-primary">
          여기에 파일을 놓으세요 (최대 {MAX_FILES}개)
        </div>
      )}
      <div className="mx-auto w-full max-w-3xl px-3 py-2.5">
        {has && (
          <div className="mb-2 space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
              {pendingFiles.map((f, i) => (
                <div
                  key={`${f.name}-${i}`}
                  className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs"
                >
                  <FileText className="h-3 w-3 shrink-0" />
                  <span className="max-w-[45vw] truncate">{f.name}</span>
                  <button onClick={() => removeAt(i)} aria-label="첨부 제거">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {pendingFiles.length > 1 && (
                <button onClick={() => setPendingFiles([])} className="text-xs text-muted-foreground underline">
                  전체 제거
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SKILL_CHIPS.map((c) => {
                const enabled = !busy && pendingFiles.length >= c.minFiles;
                return (
                  <button
                    key={c.hint}
                    onClick={() => runChip(c.hint, c.minFiles)}
                    disabled={!enabled}
                    title={pendingFiles.length < c.minFiles ? `파일 ${c.minFiles}개 이상 필요` : undefined}
                    className="rounded-full border border-border bg-card px-3 py-1 text-xs hover:bg-muted disabled:opacity-40"
                  >
                    {c.label}
                  </button>
                );
              })}
              <span className="self-center text-xs text-muted-foreground">
                또는 지시를 입력하고 전송 — 스킬 외 요청은 첨부를 참고자료로 답변합니다
              </span>
            </div>
          </div>
        )}
        {/* 검색 방법 · 답변 모델 — 드롭박스 2개, 한 줄 표시(설명 없음).
            검색 방법: 선택 시 질문 앞 prefix 로 백엔드 라우팅(기존 메커니즘 동일).
            답변 모델: 이번 질문에만 적용, 전송 후 기본값으로 복귀. */}
        <div className="mb-2 flex items-center gap-1.5">
          {(() => {
            const ModeIcon = SEARCH_MODES.find((s) => s.key === mode)?.icon ?? Database;
            return (
              <label
                className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-1 text-xs text-muted-foreground"
                title={SEARCH_MODES.find((s) => s.key === mode)?.hint ?? "검색 방법"}
              >
                <ModeIcon className="h-3.5 w-3.5 shrink-0" />
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value as SearchMode)}
                  aria-label="검색 방법 선택"
                  className="max-w-[36vw] cursor-pointer bg-transparent text-xs text-foreground outline-none"
                >
                  {SEARCH_MODES.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            );
          })()}
          {models.length > 0 && (
            <label
              className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-1 text-xs text-muted-foreground"
              title="답변에 사용할 AI 모델 — 선택한 모델이 계속 유지됩니다"
            >
              <Cpu className="h-3.5 w-3.5 shrink-0" />
              <select
                value={modelId ?? ""}
                onChange={(e) => onModelChange?.(Number(e.target.value))}
                disabled={busy}
                aria-label="답변 모델 선택"
                className="max-w-[36vw] cursor-pointer bg-transparent text-xs text-foreground outline-none disabled:opacity-50"
              >
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                    {m.is_default ? " (기본값)" : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="flex items-end gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground active:bg-muted"
            aria-label="파일 첨부"
            title="파일 첨부(여러 개 가능 · 드래그앤드롭 지원)"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.hwp,.png,.jpg,.jpeg,.gif,.webp,.bmp"
            onChange={onPickFile}
          />
          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              autoGrow();
            }}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder={has ? "첨부에 대해 질문하거나 지시를 입력하세요…" : "무엇이든 물어보세요…"}
            className="max-h-40 flex-1 resize-none rounded-lg border border-border bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
          {busy ? (
            <button
              onClick={onStop}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0E1E2D] hover:bg-[#1C3954] text-white"
              aria-label="생성 중지"
              title="생성 중지"
            >
              <Square className="h-4 w-4 fill-current" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={!text.trim() && !has}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground disabled:opacity-40"
              aria-label="전송"
            >
              <SendHorizontal className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
